import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Alert, Button, Card, Select, TextInput } from "@opendate-app/setlist";

import { useBroadcastEditContext } from "../BroadcastEditContainer";
import SparkleIcon from "../../../shared/Icons/SparkleIcon";
import { templateEvent } from "./templateEvent";

const styles = {
  stack: {
    display: "flex",
    flexDirection: "column",
    gap: 20,
    marginTop: 24,
  },
  messageCard: {
    background: "var(--sl-bg-secondary)",
  },
  messageFields: {
    display: "flex",
    flexDirection: "column",
    gap: 20,
  },
  suggestions: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 8,
    marginTop: 8,
  },
  suggestionsLabel: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    width: "100%",
    fontSize: 12,
    color: "var(--sl-text-tertiary)",
  },
  suggestionPill: (selected) => ({
    borderRadius: 16,
    maxWidth: "100%",
    whiteSpace: "normal",
    textAlign: "left",
    ...(selected && { background: "var(--sl-bg-brand-primary)", borderColor: "var(--sl-border-brand)" }),
  }),
  labelRow: {
    display: "flex",
    justifyContent: "space-between",
    width: "100%",
  },
  optional: {
    fontWeight: 400,
    color: "var(--sl-text-quaternary)",
  },
};

export const senderNameFor = (sendDomain, { namesByAddress, selectedSendDomain, senderName }) => {
  if (namesByAddress[sendDomain.id]?.trim()) return namesByAddress[sendDomain.id];
  if (!selectedSendDomain) return senderName || sendDomain.name;

  return sendDomain.name || senderName;
};

const SenderStep = ({ editedNames, setEditedNames }) => {
  const {
    team,
    venueOwnership,
    broadcast,
    updateBroadcast,
    beefreeTemplate,
    availableSendDomains,
    emailSendDomain,
    setEmailSendDomain,
    selectedSendDomain,
    setSelectedSendDomain,
    subject,
    setSubject,
    preheader,
    setPreheader,
    defaultSendDomainId,
    template,
  } = useBroadcastEditContext();

  const [nameTouched, setNameTouched] = useState(false);
  const [subjectSuggestions, setSubjectSuggestions] = useState([]);
  const { eventId } = templateEvent(template);
  const latestPreheader = useRef(preheader);
  latestPreheader.current = preheader;
  const autoPreheader = useRef(null);
  const previewRequest = useRef(0);
  const cancelPreview = () => {
    previewRequest.current += 1;
  };

  useEffect(() => cancelPreview, [eventId]);

  useEffect(() => {
    let current = true;
    setSubjectSuggestions([]);

    axios.get(`/teams/${team.id}/broadcasts/${broadcast.id}/subject_suggestions`, { params: { target: eventId } })
      .then(({ data }) => {
        if (current) setSubjectSuggestions(data.suggestions || []);
      })
      .catch(() => {});

    return () => {
      current = false;
    };
  }, [team.id, broadcast.id, eventId]);
  const senderName = emailSendDomain?.name || "";
  const namesByAddress = editedNames ?? (selectedSendDomain ? { [selectedSendDomain.id]: senderName } : {});

  const saveSender = (attributes) => {
    updateBroadcast({
      beefree_template_attributes: {
        id: beefreeTemplate.id,
        email_send_domain_attributes: {
          id: emailSendDomain?.id,
          send_domain_id: selectedSendDomain?.id,
          name: senderName,
          ...attributes,
        },
      },
    }, false);
  };

  const selectAddress = (sendDomain) => {
    const name = senderNameFor(sendDomain, { namesByAddress, selectedSendDomain, senderName });

    setSelectedSendDomain(sendDomain);
    setEmailSendDomain({ ...emailSendDomain, send_domain_id: sendDomain.id, name });
    saveSender({ send_domain_id: sendDomain.id, name });
  };

  useEffect(() => {
    if (selectedSendDomain) return;

    const defaultSendDomain = availableSendDomains.find((sendDomain) => sendDomain.id === defaultSendDomainId);
    if (defaultSendDomain) selectAddress(defaultSendDomain);
  }, []);

  const saveName = () => {
    setNameTouched(true);
    if (!senderName.trim()) return;
    if (!selectedSendDomain) return;

    saveSender({ name: senderName.trim() });
  };

  const saveIfChanged = (attribute, value) => {
    if ((broadcast[attribute] || "") === value) return;

    updateBroadcast({ [attribute]: value }, false);
  };

  const fillPreview = (nextSubject) => {
    cancelPreview();
    if (!nextSubject.trim()) return;
    if (latestPreheader.current && latestPreheader.current !== autoPreheader.current) return;

    const request = previewRequest.current;
    axios.get(`/teams/${team.id}/broadcasts/${broadcast.id}/preview_text_suggestion`, { params: { subject: nextSubject, target: eventId } })
      .then(({ data }) => {
        if (request !== previewRequest.current || !data.suggestion) return;
        if (latestPreheader.current && latestPreheader.current !== autoPreheader.current) return;

        autoPreheader.current = data.suggestion;
        setPreheader(data.suggestion);
        saveIfChanged("preheader", data.suggestion);
      })
      .catch(() => {});
  };

  return (
    <div style={styles.stack}>
      {availableSendDomains.length === 0 && (
        <Alert
          color="error"
          title="Your sending domain isn't set up yet."
          description="Fans can only get email from a verified address."
          actions={(
            <Button
              variant="link-color"
              size="sm"
              onClick={() => {
                window.location.href = `/teams/${team.id}/marketing_settings?venue_ownership_id=${venueOwnership.id}&tab=email`;
              }}
            >
              Set up a sending domain
            </Button>
          )}
        />
      )}

      <TextInput
        label="Sender name"
        placeholder={venueOwnership.venueable.name}
        value={senderName}
        error={nameTouched && !senderName.trim() ? "Add a name fans will recognize." : null}
        onChange={(event) => {
          setEmailSendDomain({ ...emailSendDomain, name: event.target.value });
          if (selectedSendDomain) setEditedNames({ ...namesByAddress, [selectedSendDomain.id]: event.target.value });
        }}
        onBlur={saveName}
      />

      <Select
        label="Sender email"
        placeholder="Choose a sending address"
        searchable={availableSendDomains.length > 8}
        options={availableSendDomains.map((sendDomain) => ({ value: sendDomain.id, label: sendDomain.full_from_address }))}
        value={selectedSendDomain?.id ?? null}
        onChange={(id) => selectAddress(availableSendDomains.find((sendDomain) => sendDomain.id === id))}
      />

      <Card style={styles.messageCard}>
        <div style={styles.messageFields}>
          <div>
            <TextInput
              label="Subject line"
              placeholder="What's the big news?"
              value={subject || ""}
              onChange={(event) => {
                cancelPreview();
                setSubject(event.target.value);
              }}
              onBlur={() => {
                if (!preheader || (broadcast.subject || "") !== (subject || "")) fillPreview(subject || "");
                saveIfChanged("subject", subject || "");
              }}
            />

            {subjectSuggestions.length > 0 && (
              <div style={styles.suggestions}>
                <div style={styles.suggestionsLabel}>
                  <SparkleIcon className="" width="12" height="12" />
                  Conductor suggestions for this audience
                </div>
                {subjectSuggestions.map((suggestion) => {
                  const selected = subject === suggestion;

                  return (
                    <Button
                      key={suggestion}
                      variant="secondary"
                      size="sm"
                      aria-pressed={selected}
                      style={styles.suggestionPill(selected)}
                      onClick={() => {
                        const nextSubject = selected ? "" : suggestion;
                        setSubject(nextSubject);
                        saveIfChanged("subject", nextSubject);
                        fillPreview(nextSubject);
                      }}
                    >
                      {suggestion}
                    </Button>
                  );
                })}
              </div>
            )}
          </div>

          <TextInput
            label={<span style={styles.labelRow}>Preview text<span style={styles.optional}>Optional</span></span>}
            placeholder="Shows next to the subject in most inboxes"
            value={preheader || ""}
            onChange={(event) => setPreheader(event.target.value)}
            onBlur={() => saveIfChanged("preheader", preheader || "")}
          />
        </div>
      </Card>
    </div>
  );
};

export default SenderStep;
